import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listContracts,
  getContract,
  signContract,
  startMilestone,
  submitMilestone,
  sendMessage,
  getMessages,
  markMessagesRead,
} from '../../services/contracts';
import type {
  ContractSummary,
  Contract,
  Milestone,
  ContractMessage,
  SubmitMilestoneRequest,
} from '../../types/contract';
import {
  getContractStatusLabel,
  getContractStatusColor,
  getMilestoneStatusLabel,
  getMilestoneStatusColor,
  getContractProgress,
  formatDate,
  formatDateTime,
  canExpertSign,
  canStartMilestone,
  canSubmitMilestone,
} from '../../types/contract';
import { formatCurrency } from '../../types/payment';

export default function ContractsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [contracts, setContracts] = useState<ContractSummary[]>([]);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [messages, setMessages] = useState<ContractMessage[]>([]);
  const [totalContracts, setTotalContracts] = useState(0);

  // UI state
  const [activeTab, setActiveTab] = useState<'active' | 'completed' | 'all'>('active');
  const [showSignModal, setShowSignModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null);
  const [processing, setProcessing] = useState(false);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    loadContracts();
  }, [activeTab]);

  async function loadContracts() {
    try {
      setLoading(true);
      setError(null);

      const statusFilter = activeTab === 'active' ? 'active' : activeTab === 'completed' ? 'completed' : undefined;
      const response = await listContracts({ status: statusFilter });
      setContracts(response.contracts);
      setTotalContracts(response.total);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }

  async function loadContractDetails(id: string) {
    try {
      setLoading(true);
      const [contract, messagesResponse] = await Promise.all([
        getContract(id),
        getMessages(id, 1, 50),
      ]);
      setSelectedContract(contract);
      setMessages(messagesResponse.messages);
      // Mark messages as read
      await markMessagesRead(id);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to load contract details');
    } finally {
      setLoading(false);
    }
  }

  async function handleSign() {
    if (!selectedContract) return;

    try {
      setProcessing(true);
      await signContract(selectedContract.id, { acknowledged: true });
      await loadContractDetails(selectedContract.id);
      setShowSignModal(false);
      loadContracts();
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to sign contract');
    } finally {
      setProcessing(false);
    }
  }

  async function handleStartMilestone(milestone: Milestone) {
    if (!selectedContract) return;

    try {
      setProcessing(true);
      await startMilestone(selectedContract.id, milestone.id);
      await loadContractDetails(selectedContract.id);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to start milestone');
    } finally {
      setProcessing(false);
    }
  }

  async function handleSubmitMilestone(data: SubmitMilestoneRequest) {
    if (!selectedContract || !selectedMilestone) return;

    try {
      setProcessing(true);
      await submitMilestone(selectedContract.id, selectedMilestone.id, data);
      await loadContractDetails(selectedContract.id);
      setShowSubmitModal(false);
      setSelectedMilestone(null);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to submit milestone');
    } finally {
      setProcessing(false);
    }
  }

  async function handleSendMessage() {
    if (!selectedContract || !newMessage.trim()) return;

    try {
      setProcessing(true);
      await sendMessage(selectedContract.id, { message: newMessage.trim() });
      setNewMessage('');
      const messagesResponse = await getMessages(selectedContract.id, 1, 50);
      setMessages(messagesResponse.messages);
    } catch (e: unknown) {
      setError((e as Error).message || 'Failed to send message');
    } finally {
      setProcessing(false);
    }
  }

  if (loading && !selectedContract) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Contract detail view
  if (selectedContract) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <button
          onClick={() => setSelectedContract(null)}
          className="mb-6 text-blue-600 hover:text-blue-700 flex items-center"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Contracts
        </button>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">
              Dismiss
            </button>
          </div>
        )}

        {/* Contract Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold">{selectedContract.title}</h1>
              <p className="text-gray-600 mt-1">
                {selectedContract.description || 'No description'}
              </p>
            </div>
            <StatusBadge
              status={selectedContract.status}
              getLabel={getContractStatusLabel}
              getColor={getContractStatusColor}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div>
              <p className="text-sm text-gray-500">Total Value</p>
              <p className="text-lg font-semibold">
                {formatCurrency(selectedContract.total_amount_cents, selectedContract.currency)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Start Date</p>
              <p className="text-lg font-semibold">{formatDate(selectedContract.start_date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">End Date</p>
              <p className="text-lg font-semibold">{formatDate(selectedContract.end_date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Platform Fee</p>
              <p className="text-lg font-semibold">{(selectedContract.platform_fee_rate * 100).toFixed(0)}%</p>
            </div>
          </div>

          {/* Signature Status */}
          <div className="mt-6 pt-6 border-t">
            <h3 className="font-semibold mb-3">Signatures</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Company</p>
                {selectedContract.company_signed_at ? (
                  <p className="text-green-600">
                    Signed on {formatDateTime(selectedContract.company_signed_at)}
                  </p>
                ) : (
                  <p className="text-yellow-600">Pending</p>
                )}
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">You (Expert)</p>
                {selectedContract.expert_signed_at ? (
                  <p className="text-green-600">
                    Signed on {formatDateTime(selectedContract.expert_signed_at)}
                  </p>
                ) : canExpertSign(selectedContract) ? (
                  <button
                    onClick={() => setShowSignModal(true)}
                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Sign Contract
                  </button>
                ) : (
                  <p className="text-yellow-600">Awaiting company signature</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Milestones */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Milestones</h2>

          <div className="space-y-4">
            {selectedContract.milestones?.map((milestone) => (
              <MilestoneCard
                key={milestone.id}
                milestone={milestone}
                contract={selectedContract}
                onStart={() => handleStartMilestone(milestone)}
                onSubmit={() => {
                  setSelectedMilestone(milestone);
                  setShowSubmitModal(true);
                }}
                processing={processing}
              />
            ))}
          </div>
        </div>

        {/* Messages */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Messages</h2>

          <div className="border rounded-lg mb-4 max-h-96 overflow-y-auto">
            {messages.length === 0 ? (
              <p className="p-4 text-gray-500 text-center">No messages yet</p>
            ) : (
              <div className="p-4 space-y-4">
                {messages.slice().reverse().map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg ${
                      msg.is_system_message
                        ? 'bg-gray-100 text-gray-600 text-sm text-center'
                        : msg.sender_type === 'expert'
                        ? 'bg-blue-100 ml-8'
                        : 'bg-gray-50 mr-8'
                    }`}
                  >
                    {!msg.is_system_message && (
                      <p className="text-xs text-gray-500 mb-1">
                        {msg.sender_type === 'expert' ? 'You' : 'Company'} &middot;{' '}
                        {formatDateTime(msg.created_at)}
                      </p>
                    )}
                    <p className={msg.is_system_message ? '' : 'text-sm'}>{msg.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedContract.status === 'active' && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || processing}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Send
              </button>
            </div>
          )}
        </div>

        {/* Sign Modal */}
        {showSignModal && (
          <SignContractModal
            contract={selectedContract}
            onClose={() => setShowSignModal(false)}
            onSign={handleSign}
            processing={processing}
          />
        )}

        {/* Submit Modal */}
        {showSubmitModal && selectedMilestone && (
          <SubmitMilestoneModal
            milestone={selectedMilestone}
            onClose={() => {
              setShowSubmitModal(false);
              setSelectedMilestone(null);
            }}
            onSubmit={handleSubmitMilestone}
            processing={processing}
          />
        )}
      </div>
    );
  }

  // Contract list view
  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">My Contracts</h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6">
        {(['active', 'completed', 'all'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 font-medium ${
              activeTab === tab
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Contract List */}
      {contracts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-16 w-16 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">No contracts yet</h2>
          <p className="text-gray-600">
            When a company offers you a contract, it will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {contracts.map((contract) => (
            <ContractCard
              key={contract.id}
              contract={contract}
              onClick={() => loadContractDetails(contract.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface ContractCardProps {
  contract: ContractSummary;
  onClick: () => void;
}

function ContractCard({ contract, onClick }: ContractCardProps) {
  const progress = getContractProgress(contract);

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold">{contract.title}</h3>
          <p className="text-sm text-gray-500">Created {formatDate(contract.created_at)}</p>
        </div>
        <StatusBadge
          status={contract.status}
          getLabel={getContractStatusLabel}
          getColor={getContractStatusColor}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-sm text-gray-500">Total Value</p>
          <p className="font-semibold">
            {formatCurrency(contract.total_amount_cents, contract.currency)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Paid</p>
          <p className="font-semibold text-green-600">
            {formatCurrency(contract.paid_amount_cents, contract.currency)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Milestones</p>
          <p className="font-semibold">
            {contract.paid_milestones}/{contract.total_milestones}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Progress</p>
          <div className="flex items-center">
            <div className="flex-1 h-2 bg-gray-200 rounded-full mr-2">
              <div
                className="h-full bg-green-500 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-sm font-semibold">{progress}%</span>
          </div>
        </div>
      </div>

      {(contract.open_disputes > 0 || contract.unread_messages > 0) && (
        <div className="mt-4 flex gap-4">
          {contract.open_disputes > 0 && (
            <span className="text-red-600 text-sm">
              {contract.open_disputes} open dispute{contract.open_disputes > 1 ? 's' : ''}
            </span>
          )}
          {contract.unread_messages > 0 && (
            <span className="text-blue-600 text-sm">
              {contract.unread_messages} unread message{contract.unread_messages > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface MilestoneCardProps {
  milestone: Milestone;
  contract: Contract;
  onStart: () => void;
  onSubmit: () => void;
  processing: boolean;
}

function MilestoneCard({ milestone, contract, onStart, onSubmit, processing }: MilestoneCardProps) {
  return (
    <div className="border rounded-lg p-4">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h4 className="font-semibold">
            {milestone.sequence_number}. {milestone.title}
          </h4>
          {milestone.description && (
            <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
          )}
        </div>
        <StatusBadge
          status={milestone.status}
          getLabel={getMilestoneStatusLabel}
          getColor={getMilestoneStatusColor}
        />
      </div>

      <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
        <div>
          <p className="text-gray-500">Amount</p>
          <p className="font-semibold">{formatCurrency(milestone.amount_cents, contract.currency)}</p>
        </div>
        <div>
          <p className="text-gray-500">Your Share</p>
          <p className="font-semibold text-green-600">
            {formatCurrency(milestone.expert_amount_cents, contract.currency)}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Due Date</p>
          <p className="font-semibold">{formatDate(milestone.due_date)}</p>
        </div>
      </div>

      {/* Revision info */}
      {milestone.status === 'revision_requested' && (
        <div className="mt-4 p-3 bg-orange-50 rounded-lg">
          <p className="text-orange-800 text-sm">
            Revision requested ({milestone.revision_count}/{milestone.max_revisions} revisions used)
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-4 flex gap-2">
        {canStartMilestone(milestone, contract) && (
          <button
            onClick={onStart}
            disabled={processing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Start Working
          </button>
        )}
        {canSubmitMilestone(milestone, contract) && (
          <button
            onClick={onSubmit}
            disabled={processing}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            Submit Work
          </button>
        )}
        {milestone.status === 'approved' && (
          <span className="px-4 py-2 text-green-600">
            Awaiting payment release from company
          </span>
        )}
        {milestone.status === 'paid' && (
          <span className="px-4 py-2 text-green-600 flex items-center">
            <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Paid on {formatDate(milestone.paid_at)}
          </span>
        )}
      </div>
    </div>
  );
}

interface StatusBadgeProps<T extends string> {
  status: T;
  getLabel: (status: T) => string;
  getColor: (status: T) => string;
}

function StatusBadge<T extends string>({ status, getLabel, getColor }: StatusBadgeProps<T>) {
  const color = getColor(status);
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    red: 'bg-red-100 text-red-800',
    orange: 'bg-orange-100 text-orange-800',
    gray: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-3 py-1 text-sm font-medium rounded-full ${colorClasses[color] || colorClasses.gray}`}>
      {getLabel(status)}
    </span>
  );
}

interface SignContractModalProps {
  contract: Contract;
  onClose: () => void;
  onSign: () => void;
  processing: boolean;
}

function SignContractModal({ contract, onClose, onSign, processing }: SignContractModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-6">Sign Contract</h2>

        <div className="mb-6">
          <h3 className="font-semibold mb-2">Contract: {contract.title}</h3>
          <p className="text-gray-600">{contract.description}</p>
        </div>

        <div className="mb-6">
          <h4 className="font-semibold mb-2">Scope of Work</h4>
          <p className="text-gray-600 whitespace-pre-wrap">{contract.scope_of_work}</p>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold mb-2">Contract Value</h4>
          <p className="text-2xl font-bold">
            {formatCurrency(contract.total_amount_cents, contract.currency)}
          </p>
          <p className="text-sm text-gray-500">
            Platform fee: {(contract.platform_fee_rate * 100).toFixed(0)}%
          </p>
        </div>

        {contract.terms_and_conditions && (
          <div className="mb-6">
            <h4 className="font-semibold mb-2">Terms and Conditions</h4>
            <div className="max-h-40 overflow-y-auto p-3 bg-gray-50 rounded-lg text-sm">
              {contract.terms_and_conditions}
            </div>
          </div>
        )}

        <div className="mb-6">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 mr-3"
            />
            <span className="text-sm text-gray-700">
              I have read and agree to the contract terms, scope of work, and payment terms.
              I understand this is a legally binding agreement.
            </span>
          </label>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={processing}
          >
            Cancel
          </button>
          <button
            onClick={onSign}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            disabled={processing || !acknowledged}
          >
            {processing ? 'Signing...' : 'Sign Contract'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface SubmitMilestoneModalProps {
  milestone: Milestone;
  onClose: () => void;
  onSubmit: (data: SubmitMilestoneRequest) => void;
  processing: boolean;
}

function SubmitMilestoneModal({ milestone, onClose, onSubmit, processing }: SubmitMilestoneModalProps) {
  const [notes, setNotes] = useState('');
  const [urls, setUrls] = useState('');

  function handleSubmit() {
    const deliverableUrls = urls
      .split('\n')
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    onSubmit({
      submission_notes: notes,
      deliverable_urls: deliverableUrls,
    });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4">
        <h2 className="text-xl font-bold mb-6">Submit Milestone</h2>

        <div className="mb-6">
          <p className="font-semibold">{milestone.title}</p>
          {milestone.deliverables && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Expected Deliverables:</p>
              <p className="text-sm">{milestone.deliverables}</p>
            </div>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Submission Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Describe what you've completed..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[100px]"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Deliverable URLs (one per line)
          </label>
          <textarea
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            placeholder="https://github.com/...&#10;https://drive.google.com/..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[80px] font-mono text-sm"
          />
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={processing}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            disabled={processing}
          >
            {processing ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
